import type { JsonObject } from "./protocol";
import { ServerError, type GameSocket } from "./socket";

/**
 * Keeps the player's save on the server (state.save). Saves are sent one at a
 * time, newest data wins: on a conflict (another device saved meanwhile) it
 * takes the stored version and saves again. Failures are retried with the
 * next save; the device's local save stays the fallback.
 */
export class ServerSave {
  private version = 0;
  private pending: JsonObject | undefined;
  private sending = false;

  constructor(private readonly socket: Pick<GameSocket, "request" | "serverHello">) {}

  /** The version the server has (from session.bootstrap). */
  setVersion(version: number): void {
    this.version = version;
  }

  save(data: JsonObject): void {
    this.pending = data;
    if (!this.sending) void this.drain();
  }

  private async drain(): Promise<void> {
    this.sending = true;
    try {
      while (this.pending) {
        const data = this.pending;
        this.pending = undefined;
        await this.send(data, true);
      }
    } finally {
      this.sending = false;
    }
  }

  private async send(data: JsonObject, retryConflict: boolean): Promise<void> {
    const limit = this.socket.serverHello?.maxStateBytes;
    if (limit !== undefined) {
      const bytes = new TextEncoder().encode(JSON.stringify(data)).length;
      if (bytes > limit) {
        // Never fits one message: keep it on the device and say so loudly.
        console.error(`server save skipped: ${bytes} bytes is over the server's limit of ${limit} (hello.maxStateBytes)`);
        return;
      }
    }
    try {
      const res = await this.socket.request("state.save", { version: this.version + 1, data });
      this.version = res.version;
    } catch (err) {
      const stored = err instanceof ServerError && err.code === "conflict" ? storedVersion(err.details) : undefined;
      if (stored !== undefined && retryConflict) {
        this.version = stored;
        await this.send(data, false);
        return;
      }
      console.warn("server save failed", err);
    }
  }
}

function storedVersion(details: JsonObject | undefined): number | undefined {
  const state = details?.state as { version?: unknown } | undefined;
  return typeof state?.version === "number" ? state.version : undefined;
}
